import { Loader2 } from "lucide-react";

import { MetricContainer } from "@/components/competition-level/MetricContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { cn } from "@/lib/utils";

const higherIsBetterFn = (m1: number, m2: number) => m1 > m2;
const lowerIsBetterFn = (m1: number, m2: number) => m1 < m2;

type MetricName =
  | "Max FB Velocity"
  | "AVG EV Against"
  | "In Zone Rate"
  | "K Rate"
  | "Walk Rate"
  | "Whiff Rate"
  | "Max Exit Velocity"
  | "AVG Exit Velocity"
  | "Chase Rate"
  | "Contact Rate"
  | "AVG EV vs FB"
  | "AVG EV vs OFF"
  | "Max Projected Distance"
  | "Average Projected Distance"
  | "Average Launch"
  | "Average Spin"
  | "Max FB IVB"
  | "Max FB Spin Rate"
  | "AVG FB Velocity"
  | "AVG FB Efficiency";

const comparisonFn: Record<MetricName, (m1: number, m2: number) => boolean> = {
  "Max FB Velocity": higherIsBetterFn,
  "AVG EV Against": lowerIsBetterFn,
  "In Zone Rate": higherIsBetterFn,
  "K Rate": higherIsBetterFn,
  "Walk Rate": lowerIsBetterFn,
  "Whiff Rate": higherIsBetterFn,
  "Max Exit Velocity": higherIsBetterFn,
  "AVG Exit Velocity": higherIsBetterFn,
  "Chase Rate": lowerIsBetterFn,
  "Contact Rate": higherIsBetterFn,
  "AVG EV vs FB": higherIsBetterFn,
  "AVG EV vs OFF": higherIsBetterFn,
  "Max Projected Distance": higherIsBetterFn,
  "Average Projected Distance": higherIsBetterFn,
  "Average Launch": higherIsBetterFn,
  "Average Spin": lowerIsBetterFn,
  "Max FB IVB": higherIsBetterFn,
  "Max FB Spin Rate": higherIsBetterFn,
  "AVG FB Velocity": higherIsBetterFn,
  "AVG FB Efficiency": higherIsBetterFn,
};

const units: Record<MetricName, string> = {
  "Max FB Velocity": "MPH",
  "AVG EV Against": "MPH",
  "In Zone Rate": "%",
  "K Rate": "%",
  "Walk Rate": "%",
  "Whiff Rate": "%",
  "Max Exit Velocity": "MPH",
  "AVG Exit Velocity": "MPH",
  "Chase Rate": "%",
  "Contact Rate": "%",
  "AVG EV vs FB": "MPH",
  "AVG EV vs OFF": "MPH",
  "Max Projected Distance": "FT",
  "Average Projected Distance": "FT",
  "Average Launch": "Deg",
  "Average Spin": "RPM",
  "Max FB IVB": "Inches",
  "Max FB Spin Rate": "",
  "AVG FB Velocity": "MPH",
  "AVG FB Efficiency": "%",
};

export interface CompetitionLevelData {
  name: MetricName;
  values: [number | undefined, number | undefined];
}

interface CompetitionLevelProps {
  title: string;
  columnNames: [string, string];
  data: CompetitionLevelData[];
  isLoading?: boolean;
}

const getPlayerBgColor = (
  metricName: MetricName,
  playerMetric: number,
  competitionLevelMetric: number,
) => {
  const comparisonFunction = comparisonFn[metricName];
  if (comparisonFunction) {
    return comparisonFunction(playerMetric, competitionLevelMetric)
      ? "bg-green-500"
      : "bg-gray-300";
  }
  return "bg-gray-300";
};

const getCompetitionLevelBgColor = (
  metricName: MetricName,
  playerMetric: number,
  competitionLevelMetric: number,
) => {
  const comparisonFunction = comparisonFn[metricName];
  if (comparisonFunction && playerMetric != competitionLevelMetric) {
    return comparisonFunction(playerMetric, competitionLevelMetric)
      ? "bg-gray-300"
      : // We can change this to bg-red-500 in case we want to bring back the red highlight when the player is better than the competition level
        "bg-gray-300";
  }
  return "bg-gray-300";
};

export const CompetitionLevel = ({
  title,
  columnNames,
  data,
  isLoading = false,
}: CompetitionLevelProps) => {
  const formatMetricValue = (value: number | undefined, label: MetricName) =>
    value !== undefined
      ? `${units[label] === "%" ? `${(value * 100).toFixed(2)}` : `${value.toFixed(2)}`} ${units[label]}`
      : "";
  return (
    <Card
      className="w-full p-1 overflow-y-auto relative"
      data-testid={`competition-level-card-${title}`}
    >
      {isLoading && (
        <div
          className="absolute inset-0 bg-white/50 flex items-center justify-center z-50"
          data-testid="loading-spinner"
        >
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}
      <CardHeader className="p-2">
        <CardTitle className="text-sm md:text-base text-center">
          {title}
        </CardTitle>
      </CardHeader>

      <CardContent className="overflow-hidden">
        <Separator className="mb-2" />

        <Table
          classes={{
            container: "!shadow-none",
          }}
          className="table-fixed w-full"
        >
          <TableHeader>
            <TableRow className="!border-b-2 border-foreground">
              <TableHead className="w-[25%] text-xs md:text-sm p-1 text-foreground text-center">
                {columnNames[0]}
              </TableHead>
              <TableHead className="w-[50%] text-xs md:text-sm p-1"></TableHead>
              <TableHead className="w-[25%] text-xs md:text-sm p-1 text-foreground text-center">
                {columnNames[1]}
              </TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {data.map(({ name: label, values }, index) => (
              <TableRow key={index}>
                <TableCell className="p-0.5 text-center border-r-2 border-foreground">
                  <MetricContainer
                    value={formatMetricValue(values[0], label)}
                    className={cn(
                      values[0] && values[1]
                        ? getPlayerBgColor(
                            label as MetricName,
                            values[0],
                            values[1],
                          )
                        : "",
                      "text-[10px] md:text-xs h-7 w-full",
                    )}
                  />
                </TableCell>

                <TableCell className="p-0.5 text-[10px] md:text-xs text-center font-noto border-r-2 border-foreground">
                  {label}
                </TableCell>

                <TableCell className="p-0.5 text-center">
                  <MetricContainer
                    value={formatMetricValue(values[1], label)}
                    className={cn(
                      values[0] && values[1]
                        ? getCompetitionLevelBgColor(
                            label as MetricName,
                            values[0],
                            values[1],
                          )
                        : "",
                      "text-[10px] md:text-xs h-7 w-full",
                    )}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};
